import { useState, useEffect, useContext } from 'react';
import { toggleReaction, getReactions, getTodayReactionUsers } from '../api/userApi';
import { AuthContext } from '../context/AuthContext';

const REACTION_EMOJIS = {
  FIRE: '🔥',
  STRONG: '💪',
  STAR: '⭐',
  HEART: '❤️',
};

export const useReactions = (targetUserId) => {
  const { currentUser } = useContext(AuthContext);

  const [todayCounts, setTodayCounts] = useState({});
  const [lifetimeCounts, setLifetimeCounts] = useState({});
  const [myReactionsToday, setMyReactionsToday] = useState([]);
  const [reactionUsers, setReactionUsers] = useState([]);
  const [processing, setProcessing] = useState(false);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isSelf = currentUser?.id === targetUserId;

  useEffect(() => {
    if (!targetUserId) return;

    getReactions(targetUserId, timeZone)
      .then((res) => {
        setTodayCounts(res.data.todayCounts || {});
        setLifetimeCounts(res.data.lifetimeCounts || {});
        setMyReactionsToday(res.data.myReactionsToday || []);
      })
      .catch((err) => console.error('Failed to load reactions', err));

    getTodayReactionUsers(targetUserId, timeZone)
      .then((data) => setReactionUsers(data || []))
      .catch((err) => console.error('Failed to load reaction users', err));
  }, [targetUserId, timeZone]);

  const updateCount = (setter, reactionType, amount) => {
    setter((prev) => {
      const current = prev[reactionType] || 0;
      const next = Math.max(0, current + amount);

      return {
        ...prev,
        [reactionType]: next,
      };
    });
  };

  const addReactionUserOptimistic = (reactionType) => {
    if (!currentUser) return;

    setReactionUsers((prev) => {
      const existingUser = prev.find((u) => String(u.userId) === String(currentUser.id));

      if (existingUser) {
        if (existingUser.reactionTypes?.includes(reactionType)) {
          return prev;
        }

        return prev.map((u) =>
          String(u.userId) === String(currentUser.id)
            ? {
                ...u,
                reactionTypes: [...(u.reactionTypes || []), reactionType],
              }
            : u,
        );
      }

      return [
        {
          userId: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          profilePicUrl: currentUser.profilePicUrl,
          reactionTypes: [reactionType],
        },
        ...prev,
      ];
    });
  };

  const removeReactionUserOptimistic = (reactionType) => {
    if (!currentUser) return;

    setReactionUsers((prev) =>
      prev
        .map((u) => {
          if (String(u.userId) !== String(currentUser.id)) return u;

          return {
            ...u,
            reactionTypes: (u.reactionTypes || []).filter((type) => type !== reactionType),
          };
        })
        .filter((u) => (u.reactionTypes || []).length > 0),
    );
  };

  const handleToggle = async (reactionType) => {
    if (isSelf || processing) return;

    const alreadyReacted = myReactionsToday.includes(reactionType);
    const amount = alreadyReacted ? -1 : 1;

    setProcessing(true);

    // Optimistic button state
    setMyReactionsToday((prev) =>
      alreadyReacted ? prev.filter((r) => r !== reactionType) : [...prev, reactionType],
    );

    // Optimistic counts
    updateCount(setTodayCounts, reactionType, amount);
    updateCount(setLifetimeCounts, reactionType, amount);

    // Optimistic social proof
    if (alreadyReacted) {
      removeReactionUserOptimistic(reactionType);
    } else {
      addReactionUserOptimistic(reactionType);
    }

    try {
      await toggleReaction(targetUserId, reactionType, timeZone);
    } catch (err) {
      console.error('Reaction toggle failed', err);

      // Rollback button state
      setMyReactionsToday((prev) =>
        alreadyReacted ? [...prev, reactionType] : prev.filter((r) => r !== reactionType),
      );

      // Rollback counts
      updateCount(setTodayCounts, reactionType, -amount);
      updateCount(setLifetimeCounts, reactionType, -amount);

      // Rollback social proof
      if (alreadyReacted) {
        addReactionUserOptimistic(reactionType);
      } else {
        removeReactionUserOptimistic(reactionType);
      }
    } finally {
      setProcessing(false);
    }
  };

  return {
    todayCounts,
    lifetimeCounts,
    myReactionsToday,
    reactionUsers,
    handleToggle,
    isSelf,
    REACTION_EMOJIS,
    processing,
  };
};